import { ApiError, EmployeeSessionRequiredError, ForbiddenError } from '@lib/api';

import { EXPORT_RANGE_MESSAGE } from './export-range';

/**
 * What the viewer shows when a read fails. Never the empty state: "there are no
 * records" is an answer, and a failure is not one.
 *
 * - `sign-in`: the employee-pool session is missing; offer the Google sign-in.
 * - `forbidden`: signed in without the permission.
 * - `rate-limited`: 30 reads a minute; say how long to wait.
 * - `retry`: the log could not be reached (5xx or the network); "Try again".
 * - `invalid`: any other refusal; the message is the answer, nothing to retry.
 */
export type FailureView =
    | { readonly kind: 'sign-in' }
    | { readonly kind: 'forbidden' }
    | {
          readonly kind: 'rate-limited';
          readonly retryAfterSeconds: number;
          readonly message: string;
      }
    | { readonly kind: 'retry' }
    | { readonly kind: 'invalid'; readonly message: string };

/** What the BFF falls back to when audit-service named no delay. */
const DEFAULT_RETRY_AFTER_SECONDS = 60;

export function rateLimitMessage(seconds: number): string {
    return `Too many queries in the last minute. Try again in ${String(seconds)} s.`;
}

export function describeFailure(error: Error): FailureView {
    if (error instanceof EmployeeSessionRequiredError) return { kind: 'sign-in' };
    if (error instanceof ForbiddenError) return { kind: 'forbidden' };
    if (error instanceof ApiError && error.status === 429) {
        const seconds = error.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_SECONDS;
        return {
            kind: 'rate-limited',
            retryAfterSeconds: seconds,
            message: rateLimitMessage(seconds),
        };
    }
    if (error instanceof ApiError && error.status < 500) {
        return { kind: 'invalid', message: error.message };
    }
    return { kind: 'retry' };
}

/** When the retry button may be pressed again, in epoch milliseconds. */
export function retryAvailableAt(failedAt: number, retryAfterSeconds: number): number {
    return failedAt + retryAfterSeconds * 1000;
}

/** A failed export, explained beside the button rather than as a page state. */
export function exportErrorMessage(error: Error): string {
    const view = describeFailure(error);
    if (view.kind === 'rate-limited') return view.message;
    if (view.kind === 'sign-in') {
        return 'Your Veevart Google sign-in has ended. Sign in again to export';
    }
    if (error instanceof ApiError && error.code === 'EXPORT_RANGE_TOO_WIDE') {
        return EXPORT_RANGE_MESSAGE;
    }
    if (error instanceof ApiError && error.code === 'EXPORT_TOO_LARGE') {
        return 'The file is too large. Choose a shorter range';
    }
    if (view.kind === 'forbidden') return 'You do not have permission to export this log';
    if (view.kind === 'invalid') return view.message;
    return 'The export could not be generated. Try again';
}
